// src/components/user/CreateUser.jsx
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
    createUser,
    getUserById,
    getUserStatus,
    updateUser,
    getRoles,
} from '../store/slices/userSlice';
import { extractList } from '../utils/response';
import {
    Box,
    TextField,
    Button,
    Typography,
    Grid,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    Skeleton,
    Autocomplete,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PhoneIcon from '@mui/icons-material/Phone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { USERS_LIST_PATH } from '../navigations/path';
import { useAuth } from '../hooks/useAuth';

// Custom Theme
const genderOptions = [
  { value: "", label: "Not Specified" },
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "O", label: "Other" },
];

const capitalizeWords = (text) => {
  if (!text) return "";
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Secure password generator
const generateSecurePassword = (length = 12) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';
    let password = '';
    const ensureChars = [
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)],
        'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)],
        '0123456789'[Math.floor(Math.random() * 10)],
        '!@#$%^&*()_+~`|}{[]:;?><,./-='[Math.floor(Math.random() * 20)],
    ];

    const array = new Uint8Array(length - ensureChars.length);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(array);
    } else {
        for (let i = 0; i < array.length; i++) {
            array[i] = Math.floor(Math.random() * 256);
        }
    }

    for (let i = 0; i < array.length; i++) {
        password += chars[array[i] % chars.length];
    }

    password = (ensureChars.join('') + password).split('').sort(() => 0.5 - Math.random()).join('');
    return password;
};

const CreateUser = () => {
    const dispatch = useDispatch();
    const { hasPermission } = useAuth();

    const canViewUser = hasPermission('user-view');
    const canEditUser = hasPermission('user-edit');

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editId = searchParams.get('edit_id');
    const viewId = searchParams.get('view');
    const isEditMode = !!editId;
    const isViewMode = !!viewId;
    const userId = editId || viewId;

    const initialFormData = {
        name: '',
        username: '',
        email: '',
        password: isEditMode || isViewMode ? '' : generateSecurePassword(),
        phone: '',
        gender: '',
        role: '',
        status: '',
        sudo_name: '',
        assigned_numbers: [],
    };
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!userId);
    const [statuses, setStatuses] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [assignedNumberInput, setAssignedNumberInput] = useState('');

    // Fetch statuses, roles, and user data
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const statusData = await dispatch(getUserStatus()).unwrap();
                setStatuses(Array.isArray(statusData) ? statusData : statusData?.list ?? []);
            } catch (error) {
                console.error("Fetch statuses error:", error);
                toast.error('Failed to load statuses.');
            }
        };

        const fetchRoles = async () => {
            try {
                setLoadingRoles(true);
                const response = await dispatch(getRoles()).unwrap();
                const roleList = extractList(response).filter(
                    (r) => (r?.name || '').toLowerCase() !== 'customer'
                );
                setRoles(roleList);
            } catch (error) {
                console.error("Fetch roles error:", error);
                toast.error('Failed to load roles.');
            } finally {
                setLoadingRoles(false);
            }
        };

        const fetchUser = async () => {
            if (!userId) return;
            if (!canEditUser && !canViewUser) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const userData = await dispatch(getUserById(userId)).unwrap();
                const user = userData?.data ?? userData;
                const userRoleId = user?.roles?.[0]?.id ?? '';
                const statusId = (user?.status && typeof user.status === 'object' && user.status.id)
                    ? user.status.id
                    : (user?.status_id ?? user?.status ?? '');
                const assignedNumbersRaw = user?.assigned_numbers;
                const assignedNumbers = Array.isArray(assignedNumbersRaw)
                    ? assignedNumbersRaw.map((item) =>
                        typeof item === 'object' && item != null && 'phone_number' in item
                            ? String(item.phone_number ?? '')
                            : String(item)
                    ).filter(Boolean)
                    : assignedNumbersRaw
                        ? [String(assignedNumbersRaw)]
                        : [];
                setFormData({
                    name: user?.name ?? '',
                    username: user?.username ?? '',
                    email: user?.email ?? '',
                    password: '',
                    phone: user?.phone ?? '',
                    gender: user?.gender ?? '',
                    role: userRoleId,
                    status: statusId,
                    sudo_name: user?.sudo_name ?? '',
                    assigned_numbers: assignedNumbers,
                });
            } catch (error) {
                console.error("Fetch user error:", error);
                toast.error('Failed to load user data.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchStatuses();
        fetchRoles();
        fetchUser();
    }, [userId]);

    const validateForm = (data) => {
        const validationErrors = {};
        if (!data.name.trim()) validationErrors.name = 'Full Name is required.';
        if (!data.username.trim()) validationErrors.username = 'Username is required.';
        else if (!/^[a-zA-Z0-9_]{3,15}$/.test(data.username)) validationErrors.username = 'Username must be 3-15 alphanumeric characters or underscores.';

        if (!data.email.trim()) validationErrors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) validationErrors.email = 'Invalid email format.';

        if (!isEditMode && !data.password.trim()) validationErrors.password = 'Initial Password is required for new users.';
        if (!data.status) validationErrors.status = 'Status is required.';
        if (!data.role) validationErrors.role = 'Role is required.'; // NEW

        return validationErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: '' }));

        if (name === 'phone') {
            const digits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: digits }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        setFormData(prev => ({ ...prev, password: newPassword }));
        setErrors(prev => ({ ...prev, password: '' }));
    };

    const handleAddAssignedNumber = () => {
        const trimmed = (assignedNumberInput || '').trim();
        if (!trimmed) return;
        setFormData((prev) => ({
            ...prev,
            assigned_numbers: [...(prev.assigned_numbers || []), trimmed],
        }));
        setAssignedNumberInput('');
    };

    const handleRemoveAssignedNumber = (index) => {
        setFormData((prev) => {
            const next = [...(prev.assigned_numbers || [])];
            next.splice(index, 1);
            return { ...prev, assigned_numbers: next };
        });
    };

    const handleCopyPassword = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(formData.password)
                .then(() => setSnackbarOpen(true))
                .catch(() => toast.error('Failed to copy password.'));
        } else {
            const tempInput = document.createElement('input');
            tempInput.value = formData.password;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationData = isEditMode && !formData.password.trim()
            ? { ...formData, password: 'DUMMY_PASSWORD' }
            : formData;

        const validationErrors = validateForm(validationData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length) {
            toast.error('Please fix the errors in the form.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = isEditMode && !formData.password.trim()
                ? Object.keys(formData).reduce((acc, key) => {
                    if (key !== 'password') {
                        if (key === 'status') {
                            acc['status_id'] = formData[key];
                        } else if (key === 'role') {
                            // Send role name instead of ID
                            const selectedRole = roles.find(r => r.id === formData[key]);
                            acc['role'] = selectedRole?.name || '';
                        } else {
                            acc[key] = formData[key];
                        }
                    }
                    return acc;
                }, {})
                : {
                    ...formData,
                    status_id: formData.status,
                    // Send role name
                    role: roles.find(r => r.id === formData.role)?.name || '',
                    status: undefined,
                    role_id: undefined, // remove if not needed
                };

            let response;
            if (isEditMode) {
                response = await dispatch(updateUser({ id: userId, userData: payload })).unwrap();
                toast.success(`User ${formData.name} updated successfully!`);
            } else {
                response = await dispatch(createUser(payload)).unwrap();
                toast.success(`User ${formData.name} created successfully! (ID: ${response.userId || 'N/A'})`);
            }

            navigate(USERS_LIST_PATH);
        } catch (error) {
            console.error("API submission error:", error);
            if (error.response?.data?.errors) {
                error.response.data.errors.forEach((err) => toast.error(err.message));
            } else {
                toast.error(isEditMode ? 'Failed to update user.' : 'Failed to create user. Please check the data.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    const formTitle = isViewMode
        ? `View User: ${formData.name}`
        : isEditMode
            ? `Edit User: ${formData.name}`
            : 'Add User';
    const submitButtonText = isEditMode ? 'Update User' : 'Add User';
    const isFormDisabled = isViewMode || isSubmitting;

    return (
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, bgcolor: 'background.paper', minHeight: '100vh' }}>
                {(isViewMode || isEditMode) && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(USERS_LIST_PATH)}
                        sx={{ mb: 2 }}
                    >
                        Back
                    </Button>
                )}
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4 }}>
                    {formTitle}
                </Typography>

                <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, maxWidth: '900px', mx: 'auto' }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 3, borderBottom: 2, borderColor: 'divider', pb: 1, color: 'primary.main' }}>
                        User Details
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Sudo Name"
                                    name="sudo_name"
                                    value={formData.sudo_name}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.sudo_name}
                                    helperText={errors.sudo_name}
                                    placeholder="Sudo name of the user"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.email}
                                    helperText={errors.email || (isEditMode ? '' : "Try 'fail@example.com' to simulate an API error.")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AlternateEmailIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField

                                    fullWidth
                                    label="Username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.username}
                                    helperText={errors.username || "Must be unique and 3-15 alphanumeric characters."}
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={genderOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={genderOptions.find((g) => g.value === formData.gender) || null}
                                    onChange={(event, newValue) => {
                                    handleChange({
                                        target: {
                                        name: "gender",
                                        value: newValue ? newValue.value : ""
                                        }
                                    });
                                    }}
                                    disabled={isFormDisabled}
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Gender"
                                        fullWidth
                                    />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField

                                    fullWidth
                                    label={isEditMode ? "New Password (Leave blank to keep old)" : "Initial Password"}
                                    name="password"
                                    type="text"
                                    value={formData.password}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.password}
                                    helperText={errors.password || (isEditMode ? "Leave blank to keep current password." : "Securely generated default password.")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <VpnKeyIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    onClick={handleGeneratePassword}
                                                    startIcon={<VisibilityOffIcon />}
                                                    color="primary"
                                                    size="small"
                                                    sx={{ mr: 1, whiteSpace: 'nowrap' }}
                                                    disabled={isFormDisabled}
                                                >
                                                    Generate
                                                </Button>
                                                <IconButton
                                                    onClick={handleCopyPassword}
                                                    edge="end"
                                                    aria-label="copy password"
                                                    color="primary"
                                                    size="small"
                                                    disabled={isFormDisabled}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth error={!!errors.role} disabled={isFormDisabled}>
                                    <Autocomplete
                                        disablePortal
                                        id="role-select"
                                        options={loadingRoles ? [] : roles}           // ← important during loading
                                        getOptionLabel={(option) => option.name}      // what to show in list & input
                                        value={
                                            roles.find((r) => r.id === formData.role) || null
                                        }
                                        onChange={(event, newValue) => {
                                            // newValue is the whole object or null
                                            handleChange({
                                                target: {
                                                    name: 'role',
                                                    value: newValue ? newValue.id : '',
                                                },
                                            });
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        disabled={isFormDisabled || loadingRoles}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Role"
                                                error={!!errors.role}
                                                helperText={errors.role}
                                            // You can add placeholder, variant="outlined", size="small", etc.
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                {option.name}
                                            </li>
                                        )}
                                        // Optional: better UX when many roles
                                        autoHighlight
                                        selectOnFocus
                                        clearOnEscape
                                    // loading={loadingRoles}   ← shows loading spinner in popup (MUI v5.15+)
                                    />

                                    {/* If you prefer the old error placement style */}
                                    {!!errors.role && !loadingRoles && (
      <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
        {errors.role}
      </Typography>
    )}
                                </FormControl>
                            </Grid>
                            {/* phone field */}

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    variant="outlined"
                                    disabled={isFormDisabled}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon color="primary" />
                                            </InputAdornment>
                                        ),
                                        inputProps: { maxLength: 15 },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={statuses}
                                    getOptionLabel={(option) => capitalizeWords(option.name || "")}
                                    value={statuses.find((s) => s.id === formData.status) || null}
                                    onChange={(event, newValue) => {
                                    handleChange({
                                        target: {
                                        name: "status",
                                        value: newValue ? newValue.id : ""
                                        }
                                    });
                                    }}
                                    disabled={isFormDisabled}
                                    noOptionsText="No statuses available"
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="User Status"
                                        error={!!errors.status}
                                        helperText={errors.status}
                                        fullWidth
                                    />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={8}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    User Assigned Numbers (optional)
                                </Typography>
                                <Box sx={{width: '100%'}}>
                                {isFormDisabled ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
                                        {(formData.assigned_numbers || []).length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">None</Typography>
                                        ) : (
                                            (formData.assigned_numbers || []).map((num, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={typeof num === 'string' ? num : (num?.phone_number ?? String(num ?? ''))}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))
                                        )}
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', mb: 1 }}>
                                            <TextField
                                                fullWidth
                                                // size="small"
                                                type='number'
                                                placeholder="Add a number"
                                                value={assignedNumberInput}
                                                onChange={(e) => setAssignedNumberInput(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAssignedNumber())}
                                                // sx={{ width: 400 }}
                                                inputProps={{ maxLength: 15 }}
                                            />
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                 size="medium"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddAssignedNumber}
                                                disabled={!assignedNumberInput.trim()}
                                            >
                                                Add
                                            </Button>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {(formData.assigned_numbers || []).map((num, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={typeof num === 'string' ? num : (num?.phone_number ?? String(num ?? ''))}
                                                    size="small"
                                                    onDelete={() => handleRemoveAssignedNumber(idx)}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                                </Box>
                            </Grid>

                            {!isViewMode && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            startIcon={isSubmitting ? null : <PersonAddIcon />}
                                            disabled={isSubmitting}
                                            sx={{ py: 1.5, px: 4, borderRadius: 2, boxShadow: 6, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 10 } }}
                                        >
                                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </form>
                </Paper>

                <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 1 }}>
                        Password copied to clipboard!
                    </Alert>
                </Snackbar>
            </Box>
    );
};

export default CreateUser;